import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCar, FaTrash } from "react-icons/fa";
import { HiOutlineRectangleGroup } from "react-icons/hi2";
import { useDispatch } from "react-redux";
import { vehicleGroupService } from "../../Services/vehicle.group.service";
import { vehicleService } from "../../Services/vehicle.service";
import { odometersService } from "../../Services/odometers.service";
import { useSelected } from "../../contexts/SelectedContext";
import { Filters } from "../../components/Filters";
import SyncFusionDateTimePicker from "../Components/SyncFusionDateTimePicker/SyncFusionDateTimePicker";
import { ToastComponent } from "../Components/ToastComponent";
import { Modal, Button } from "react-bootstrap";

export const RegisterOdometer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch(); /** eslint-disable-next-line */
  dispatch({ type: "title", value: t("MAINTENANCES.REGISTER_KM.TITLE") });

  const { selectedValues } = useSelected();
  const selectedGroup = selectedValues.veiculos?.[0] || 0;
  const selectedVehicle = selectedValues.veiculosGroups?.[0] || 0;

  const [vehicleGroups, setVehicleGroups] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastParams, setToastParams] = useState(
    JSON.stringify({ show: false }),
  );

  const [registerDate, setRegisterDate] = useState(null);
  const [searchStartDate, setSearchStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d;
  });
  const [searchEndDate, setSearchEndDate] = useState(() => new Date());
  const [odometer, setOdometer] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const selectedVehicleLabel = useMemo(() => {
    const vehicle = vehicles.find((item) => item.vehicleId === selectedVehicle);
    return vehicle?.plate || vehicle?.vehicleName || vehicle?.name || "";
  }, [selectedVehicle, vehicles]);

  const fetchVehicleGroups = async () => {
    try {
      setFiltersLoading(true);
      const groups = await vehicleGroupService.fetchGroups();
      if (Array.isArray(groups)) {
        setVehicleGroups(groups);
        await fetchVehiclesByGroup(
          groups.length === 1 ? groups[0].vehicleGroupId : 0,
        );
      } else {
        setVehicleGroups([]);
        await fetchVehiclesByGroup(0);
      }
    } catch (error) {
      console.log(error);
      setVehicleGroups([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  // FIX: o endpoint vehicles/groups/vehicles passou a rejeitar GroupId=0
  // (422 "GroupId é inválido e deve ser maior que zero"), mas confirmado
  // com o backend (Reginaldo) que OMITIR o parâmetro groupId inteiramente
  // retorna todos os veículos normalmente. Então para "TODOS" simplesmente
  // não mandamos o parâmetro, em vez de iterar grupo por grupo.
  const fetchVehiclesByGroup = async (groupId) => {
    setIsLoadingVehicles(true);
    try {
      const isAllGroups =
        !groupId || groupId === "TODOS" || Number(groupId) === 0;

      const response = await vehicleService.fetchVehicles(
        isAllGroups ? undefined : groupId,
      );
      setVehicles(Array.isArray(response) ? response : []);
    } catch (error) {
      console.log(error);
      setVehicles([]);
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  useEffect(() => {
    if (selectedGroup !== null && selectedGroup !== undefined) {
      fetchVehiclesByGroup(selectedGroup);
    }
  }, [selectedGroup]);

  useEffect(() => {
    fetchVehicleGroups();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  const toLocalISOString = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.000`;
  };

  const handleRegister = async () => {
    if (
      !selectedVehicle ||
      selectedVehicle === "TODOS" ||
      selectedVehicle === 0
    ) {
      showToast(
        "Erro",
        "Selecione um veículo antes de registrar o KM.",
        "Danger",
      );
      return;
    }

    const normalizedOdometer = Number(String(odometer).replace(/\D/g, ""));
    if (!normalizedOdometer) {
      showToast("Erro", "Informe um KM válido para registrar.", "Danger");
      return;
    }

    setIsLoading(true);
    try {
      const response = await odometersService.createOdometer({
        VehicleId: Number(selectedVehicle),
        VehicleOdometerId: 0,
        OdometerDateTime: toLocalISOString(registerDate ?? new Date()),
        OdometerValue: normalizedOdometer,
      });

      if (response !== null) {
        showToast(
          "Sucesso",
          `KM ${normalizedOdometer.toLocaleString("pt-BR")} registrado para ${selectedVehicleLabel || `Veículo ${selectedVehicle}`}.`,
          "Success",
        );
        setOdometer("");
        setRegisterDate(null);
        await handleSearch(selectedVehicle);
      } else {
        showToast("Erro", "Não foi possível registrar o KM.", "Danger");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || "Ocorreu um erro ao registrar o KM.";
      showToast("Erro", msg, "Danger");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (vehicleIdOverride) => {
    const vehicleId = vehicleIdOverride ?? selectedVehicle;
    if (!vehicleId || vehicleId === "TODOS" || Number(vehicleId) === 0) {
      showToast("Erro", "Selecione um veículo antes de buscar.", "Danger");
      return;
    }
    setIsLoading(true);
    setHasSearched(true);
    try {
      const filters = {
        VehicleId: Number(vehicleId),
      };
      // FIX: antes cortávamos para "YYYY-MM-DD" (.split("T")[0]), o que fazia
      // o backend falhar ao converter para DateTimeOffset e cair no default
      // 0001-01-01T00:00:00, gerando o erro "Error converting data type
      // datetimeoffset to datetime". Agora enviamos data+hora completas.
      if (searchStartDate) filters.StartDate = toLocalISOString(searchStartDate);
      if (searchEndDate) filters.EndDate = toLocalISOString(searchEndDate);

      const response = await odometersService.fetchOdometers(filters);
      const list = Array.isArray(response)
        ? response
        : response
          ? [response]
          : [];
      setSearchResults(list);

      showToast(
        list.length ? "Resultados encontrados" : "Nenhum resultado",
        list.length
          ? `${list.length} registro(s) localizado(s).`
          : "Não há registros para os filtros selecionados.",
        list.length ? "Success" : "Warning",
      );
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Ocorreu um erro ao buscar os registros.";
      showToast("Erro", msg, "Danger");
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    setShowDeleteModal(false);
    setIsLoading(true);
    try {
      await odometersService.deleteOdometer(deleteId);
      showToast("Sucesso", "Registro excluído com sucesso.", "Success");
      setSearchResults((prev) =>
        prev.filter(
          (r) => (r.vehicleOdometerId ?? r.VehicleOdometerId) !== deleteId,
        ),
      );
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Ocorreu um erro ao excluir o registro.";
      showToast("Erro", msg, "Danger");
    } finally {
      setIsLoading(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <p>{t("MAINTENANCES.REGISTER_KM.DESCRIPTION")}</p>

      <div className="filter-maintenance">
        <Filters
          id={"veiculos"}
          isMulti={false}
          title={t("VIEW_MAP_FILTERS.GROUPS")}
          hasSubTitle={true}
          subTitle={t("VIEW_MAP_FILTERS.ALL_GROUPS")}
          options={vehicleGroups || []}
          icon={HiOutlineRectangleGroup}
          hasIcon={true}
          pageTitle={"maintenances_scheduling"}
          isLoading={filtersLoading}
          onChange={(groupId) =>
            fetchVehiclesByGroup(groupId === "TODOS" ? 0 : groupId)
          }
        />
        <Filters
          id={"veiculosGroups"}
          isMulti={false}
          title={t("VIEW_MAP_FILTERS.VEHICLES")}
          hasSubTitle={true}
          subTitle={t("VIEW_MAP_FILTERS.ALL_VEHICLES")}
          options={vehicles || []}
          icon={FaCar}
          hasIcon={true}
          pageTitle={"maintenances_scheduling"}
          isLoading={isLoadingVehicles}
          disableTodos={true}
        />
      </div>

      <h3 className="h3-light">{t("MAINTENANCES.REGISTER_KM.REGISTER")}</h3>
      <div className="filter-maintenance">
        <div className="field">
          <SyncFusionDateTimePicker
            id="datetimepickerRegisterStart"
            value={registerDate}
            onChange={setRegisterDate}
            placeholder={t("SEARCH.INITIAL_DATE_TIME")}
            step={30}
          />
        </div>

        <div className="filter">
          <div>
            <div className="icon-filter">
              <FaCar size={20} />
            </div>
            <span className="title">KM</span>
          </div>
          <div className="filter-select-container">
            <input
              type="number"
              step="1"
              min="0"
              value={odometer}
              onChange={(event) => setOdometer(event.target.value)}
              placeholder="Informe o KM"
              className="filter-select"
              style={{ backgroundImage: "none" }}
            />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleRegister}
          disabled={isLoading}
        >
          {t("MAINTENANCES.REGISTER_KM.REGISTER")}
        </button>
      </div>

      <h3 className="h3-light">
        {t("MAINTENANCES.REGISTER_KM.SEARCH_FILTER")}
      </h3>
      <div className="filter-maintenance">
        <div className="field">
          <SyncFusionDateTimePicker
            id="datetimepickerSearchStart"
            value={searchStartDate}
            onChange={setSearchStartDate}
            placeholder={t("SEARCH.INITIAL_DATE_TIME")}
            step={30}
          />
          <SyncFusionDateTimePicker
            id="datetimepickerSearchEnd"
            value={searchEndDate}
            onChange={setSearchEndDate}
            placeholder={t("SEARCH.FINAL_DATE_TIME") || "Data final"}
            step={30}
          />
        </div>

        <button
          type="button"
          className="btn btn-primary"
          onClick={() => handleSearch()}
          disabled={isLoading}
        >
          {t("MAINTENANCES.REGISTER_KM.SEARCH")}
        </button>
      </div>

      <div className="mt-4">
        <h4 className="h3-light">
          {hasSearched ? "Resultados da busca" : "Registros recentes"}
        </h4>
        {searchResults.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th>Placa</th>
                  <th>KM</th>
                  <th>Data</th>
                  <th>Registrado por</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((entry) => {
                  const id = entry.vehicleOdometerId ?? entry.VehicleOdometerId;
                  return (
                    <tr key={id}>
                      <td>{entry.vehiclePlate ?? entry.VehiclePlate}</td>
                      <td>
                        {(
                          entry.odometerValue ?? entry.OdometerValue
                        )?.toLocaleString("pt-BR")}
                      </td>
                      <td>
                        {(entry.registerDate ?? entry.RegisterDate)
                          ? new Date(
                              entry.registerDate ?? entry.RegisterDate,
                            ).toLocaleString("pt-BR")
                          : "-"}
                      </td>
                      <td>
                        {entry.registeredByUser ??
                          entry.RegisteredByUser ??
                          "-"}
                      </td>
                      <td>
                        <button
                          className="btn btn-link p-0"
                          data-delete-action="true"
                          aria-label="Excluir"
                          style={{ color: "var(--text-primary)" }}
                          onClick={() => handleDeleteConfirm(id)}
                        >
                          <FaTrash size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted">Nenhum registro disponível no momento.</p>
        )}
      </div>

      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Body>{t("DELETE_MODAL.CONFIRM_DELETE")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t("MAINTENANCES.MAINTENANCE_ITEM.BTN_CANCEL")}
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            {t("MAINTENANCES.MAINTENANCE_ITEM.FIELD_TABLE_DELETE")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
